'use client';
import { FC } from 'react';
import ContactBanner from '@/components/common/layout/contact-banner/contact-banner';
import MobileNavigationItems from '@/components/common/layout/mobile-navigation/mobile-navigation-items';
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@/components/ui';
import useHeaderContent from '@/hooks/use-header-content';
import { useResolveThemeColor } from '@/hooks/use-resolve-theme-color';
import { useTranslation } from '@/providers/app-provider/app-provider';
import { useStudioPreviewPortalRoot } from '@/providers/studio-preview-portal-provider';
import { cn } from '@/utils';
import { studioPreviewPortalModalClassNames } from '@/utils/studio-preview-portal-modal';
import LanguageSelector from '../language-selector/language-selector';

type MobileMenuDrawerProps = {
  isOpen: boolean;
  toggleDrawer: () => void;
};

const MobileMenuDrawer: FC<MobileMenuDrawerProps> = ({ isOpen, toggleDrawer }) => {
  const { t } = useTranslation();
  const headerContent = useHeaderContent();
  const portalRoot = useStudioPreviewPortalRoot();
  const resolveThemeColor = useResolveThemeColor();

  const chromeBg = resolveThemeColor(
    headerContent.mainBackground ?? headerContent.background ?? 'content1',
  );
  const chromeFg = resolveThemeColor(headerContent.mainRowTextColor ?? 'foreground');
  const constrainModalToStudioPortal = portalRoot != null;

  return (
    <>
      <Modal
        aria-labelledby="modal-title"
        isOpen={isOpen}
        hideCloseButton
        size="full"
        portalContainer={portalRoot ?? undefined}
        classNames={constrainModalToStudioPortal ? studioPreviewPortalModalClassNames : undefined}
      >
        <ModalContent
          className={cn(
            'flex min-h-0 flex-col !rounded-none bg-transparent',
            constrainModalToStudioPortal && 'h-full max-h-full',
          )}
          style={{ backgroundColor: chromeBg, color: chromeFg }}
        >
          <ContactBanner />
          <ModalHeader className="flex items-center justify-between pr-4 text-inherit">
            <p>{t?.common.layout.menu}</p>
            {!headerContent.contactBanner && <LanguageSelector />}
          </ModalHeader>
          <ModalBody className="min-h-0 flex-1 overflow-y-auto px-6 py-2">
            <div className="h-full overflow-y-auto pr-2">
              <MobileNavigationItems onNavigate={toggleDrawer} />
            </div>
          </ModalBody>
          <ModalFooter className="flex justify-center">
            <Button color="primary" className="w-4/5 text-white" onClick={toggleDrawer}>
              {t?.common.layout.close}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default MobileMenuDrawer;
